import { CalculationResult, Inputs, YEAR_ONE_MONTHS } from './roiCalculations'

export interface YearOneHeadline {
  /** Reps hired ("signed") in Months 1–12. */
  signed: number
  /** Reps who reach day 90 inside Months 1–12. */
  day90: number
  /** New accounts sold in year one, after pull-through. */
  accounts: number
  /** Revenue from one account over its first 12 months, after attrition. */
  revenuePerAccount: number
  /** Year-one accounts × their first-12-month revenue. */
  accountRevenue: number
}

/**
 * Revenue one account brings in over its first 12 months: the initial sale
 * plus the residual payments that follow, shrinking at the attrition rate.
 * Monthly: 11 payments after the sale. Quarterly: 3 renewals.
 */
export function firstTwelveMonthRevenue(inputs: Inputs): number {
  const ticket = Math.max(0, inputs.ticket)
  const residual = Math.max(0, inputs.residualTicket || 0)
  if (!residual) return ticket
  if (inputs.revenueCadence === 'monthly') {
    const keep = 1 - Math.min(1, Math.max(0, (inputs.monthlyAttrition || 2) / 100))
    let sum = 0
    for (let k = 0; k < 11; k++) sum += Math.pow(keep, k)
    return ticket + residual * sum
  }
  if (inputs.revenueCadence === 'quarterly') {
    const keep =
      1 - Math.min(1, Math.max(0, (inputs.quarterlyAttrition || 5) / 100))
    return ticket + residual * (keep + keep * keep + keep * keep * keep)
  }
  return ticket
}

/**
 * The year-one numbers summer pest operators track: reps signed vs reps who
 * reach day 90, accounts sold, and what those accounts are worth over their
 * first 12 months. Scenario toggles scale the same way as the summary cards.
 */
export function yearOneHeadline(
  results: CalculationResult,
  inputs: Inputs,
  scenario: { halfSales: boolean; doubleAttrition: boolean },
): YearOneHeadline {
  const rows = results.rows.slice(1, YEAR_ONE_MONTHS + 1)
  const signed = rows.reduce((s, r) => s + r.hires, 0)
  const repFactor = scenario.doubleAttrition ? 0.5 : 1
  // active90d holds the cohort entering day 90+ that month, so the sum is
  // every rep who reaches day 90 inside year one.
  const day90 = rows.reduce((s, r) => s + r.active90d, 0) * repFactor
  const salesFactor =
    (scenario.halfSales ? 0.5 : 1) * (scenario.doubleAttrition ? 0.5 : 1)
  const accounts =
    rows.reduce((s, r) => s + r.totalSales * r.realizationRate, 0) *
    salesFactor
  const revenuePerAccount = firstTwelveMonthRevenue(inputs)
  return {
    signed,
    day90,
    accounts,
    revenuePerAccount,
    accountRevenue: accounts * revenuePerAccount,
  }
}
